import * as React from 'react';
import { VtxColumnProps } from '@vertexinc/vtx-ui-react-component-library';
import {
  CustomerSettingsContext,
  DateFormatEnum,
  DateOptions,
  formatDate,
  settingCustomerCustomField,
  StatusIndicator,
} from '@vcd-ecm-ui/Common';
import {
  FilterDropdownProps,
  FilterTypeEnum,
  GetActiveVertexColumns,
  SaveFiltersSorterEnum,
  SearchFilter,
  SelectFilter,
  ShowBreadCrumbsEnum,
  TableSettings,
} from '@vcd-ecm-ui/GenericGrid';
import GridRowData from './GridRowData';

// Used by Grid - Display Dates in Local Date
const dateFormat: DateOptions = { dateFormat: DateFormatEnum.MDY, dateSeperator: '/', utcDate: false };

// Used by Date Controls in Filters
const dateFormatString = 'MM/DD/YYYY';

const columns: VtxColumnProps<GridRowData>[] = [
  {
    title: 'Customer Code',
    dataIndex: 'customerCode',
    fixed: 'left',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Customer Code" dataIndex="customerCode" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Customer Name',
    dataIndex: 'customerName',
    fixed: 'left',
    className: 'reportColumnStyle',
    ellipsis: true,
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Customer Name" dataIndex="customerName" props={props} />,
  },
  {
    title: 'Parent Customer',
    dataIndex: 'parentCustomerCode',
    fixed: 'left',
    sorter: true,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Parent Customer" dataIndex="parentCustomerCode" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Taxpayer',
    dataIndex: 'taxPayerName',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Taxpayer" dataIndex="taxPayerName" props={props} />,
    sorter: true,
    className: 'reportColumnStyle',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    sorter: true,
    filters: [
      {
        text: 'Approved',
        value: 'APPROVED',
      },
      {
        text: 'Draft',
        value: 'DRAFT',
      },
      {
        text: 'Pending',
        value: 'PENDING',
      },
    ],
    width: 100,
    // eslint-disable-next-line react/destructuring-assignment
    render: (value, row: GridRowData, index: number) => <StatusIndicator status={value.toString()} />,
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SelectFilter multiple={false} title="Status" dataIndex="status" props={props} />,
  },
  {
    title: 'Start Date',
    dataIndex: 'startDate',
    render: (value) => formatDate(value, dateFormat),
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Date} dateFormat={dateFormatString} title="Start Date" dataIndex="startDate" props={props} />,
    width: 135,
    sorter: true,
  },
  {
    title: 'End Date',
    dataIndex: 'endDate',
    render: (value) => formatDate(value, dateFormat),
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Date} dateFormat={dateFormatString} title="End Date" dataIndex="endDate" props={props} />,
    width: 130,
    sorter: true,
  },
  {
    title: 'Exemptions',
    dataIndex: 'numCertificates',
    sorter: true,
    filters: [
      {
        text: 'True',
        value: true,
      },
      {
        text: 'False',
        value: false,
      },
    ],
    // Translate the numCertificates Filters from Boolean to Numerical
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SelectFilter multiple={false} title="Exemptions" convertBooleanToNumerical filterType={FilterTypeEnum.Boolean} dataIndex="numCertificates" props={props} />,
    className: 'reportColumnStyle',
  },
  {
    title: 'Custom Field 1',
    dataIndex: 'customField1',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 1" dataIndex="customField1" props={props} />,
  },
  {
    title: 'Custom Field 2',
    dataIndex: 'customField2',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 2" dataIndex="customField2" props={props} />,
  },
  {
    title: 'Custom Field 3',
    dataIndex: 'customField3',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 3" dataIndex="customField3" props={props} />,
  },
  {
    title: 'Custom Field 4',
    dataIndex: 'customField4',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 4" dataIndex="customField4" props={props} />,
  },
  {
    title: 'Custom Field 5',
    dataIndex: 'customField5',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 5" dataIndex="customField5" props={props} />,
  },
  {
    title: 'Custom Field 6',
    dataIndex: 'customField6',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 6" dataIndex="customField6" props={props} />,
  },
  {
    title: 'Custom Field 7',
    dataIndex: 'customField7',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 7" dataIndex="customField7" props={props} />,
  },
  {
    title: 'Custom Field 8',
    dataIndex: 'customField8',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 8" dataIndex="customField8" props={props} />,
  },
  {
    title: 'Custom Field 9',
    dataIndex: 'customField9',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 9" dataIndex="customField9" props={props} />,
  },
  {
    title: 'Custom Field 10',
    dataIndex: 'customField10',
    sorter: true,
    className: 'reportColumnStyle',
    // eslint-disable-next-line max-len
    filterDropdown: (props: FilterDropdownProps) => <SearchFilter filterType={FilterTypeEnum.Text} title="Custom Field 10" dataIndex="customField10" props={props} />,
  },
  {
    title: 'Action',
    dataIndex: 'action',
    fixed: 'right',
    width: 100,
  },
];

// Additional Table Settings - Will add the the Table Settings extracted from Columns
export const additionalTableSettings: TableSettings = {
  version: 'v1',
  hideDisabledColumns: false,
  saveFiltersSorter: SaveFiltersSorterEnum.SESSION_STORAGE,
  showBreadcrumbs: ShowBreadCrumbsEnum.ALL,
  columns: [
    {
      dataIndex: 'action',
      canChangePin: false,
      canMove: false,
      canChangeVisibilty: false,
    },
  ],
};

const getDefaultColumns = (customerSettingsContext: CustomerSettingsContext): VtxColumnProps<GridRowData>[] => GetActiveVertexColumns(
  columns,
  customerSettingsContext,
  settingCustomerCustomField,
  'Custom Field',
);

export default getDefaultColumns;
